import React from "react";
import styled from "styled-components";
import { T, FONTS, FS, BP } from "../../HearingBundles/tokens";
import { Icon } from "../components/Icons";
import {
  CheckboxContainer,
  CheckBoxLabel,
  CustomCheckbox,
} from "../../../FormInputs";
import { ErrorLabel } from "../../../CustomSelect/CustomSelect";
import { StyledInput } from "../../../../booking_dashboard/WalkinBooking/Styles";

export const buildBundleTabsFromConfigs = (bundleConfigs) => {
  return bundleConfigs
    .map((cfg, i) => ({
      code: cfg.prefix?.trim() || String.fromCharCode(65 + i),
      name:
        cfg.subFolderName?.trim() || `Bundle ${String.fromCharCode(65 + i)}`,
      prefix: cfg.prefix?.trim() || String.fromCharCode(65 + i),
    }))
    .filter((_, i) => i < 5);
};

const Step1NamePurpose = ({
  name,
  setName,
  bundleType,
  setBundleType,
  hearing,
  setHearing,
  bundleCount,
  setBundleCount,
  bundleConfigs,
  setBundleConfigs,
  error,
  setError,
  checked,
  setChecked,
  addFooterPageNumbers = true,
  setAddFooterPageNumbers = () => {},
}) => {
  const count = parseInt(bundleCount, 10) || 1;

  const handleCountChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      setBundleCount("");
      return;
    }
    const newCount = Number(value);
    if (newCount < 1 || newCount > 5) {
      setError("Folder count must be between 1 and 5");
      return;
    }
    setError("");
    setBundleCount(String(newCount));
    setBundleConfigs((prev) =>
      Array.from({ length: newCount }, (_, i) => ({
        subFolderName: prev[i]?.subFolderName ?? "",
        prefix: prev[i]?.prefix ?? "",
      })),
    );
  };

  const updateConfig = (index, field, value) => {
    setBundleConfigs((prev) =>
      prev.map((cfg, i) => (i === index ? { ...cfg, [field]: value } : cfg)),
    );
    setError("");
  };

  return (
    <Container>
      <Grid3>
        <FieldGroup>
          <FieldLabel htmlFor="bundle-name">Bundle Name *</FieldLabel>
          <StyledInput
            variant="outlined"
            id="bundle-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Final Hearing Trial Bundle — June 2026"
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="hearing">Prepared For (optional)</FieldLabel>
          <StyledInput
            variant="outlined"
            id="hearing"
            value={hearing}
            onChange={(e) => setHearing(e.target.value)}
            placeholder="e.g. Final Hearing on 12 Jun 2026"
          />
        </FieldGroup>
      </Grid3>

      <CheckboxGroup>
        <CheckboxContainer>
          <CustomCheckbox
            id="create-sub-folder"
            checked={checked}
            onChange={(e) => setChecked(e?.target?.checked)}
          />
          <CheckBoxLabel htmlFor="create-sub-folder">
            Sub-folder(s)
          </CheckBoxLabel>
        </CheckboxContainer>

        <CheckboxItem>
          <CheckboxContainer>
            <CustomCheckbox
              id="add-footer-page-numbers"
              checked={addFooterPageNumbers}
              onChange={(e) => setAddFooterPageNumbers(e?.target?.checked)}
            />
            <CheckBoxLabel htmlFor="add-footer-page-numbers">
              Include Core Bundle Pagination
            </CheckBoxLabel>
          </CheckboxContainer>
          <CheckboxHelperText>
            Add page numberings of core bundle on the footer.
          </CheckboxHelperText>
        </CheckboxItem>
      </CheckboxGroup>

      {checked && (
        <SectionDivider>
          <SectionLabel>Bundle Structure</SectionLabel>
          <HelperText>
            Choose how many sub-bundles this Trial Bundle contains (1–5), then
            give each one a name and a short prefix used for tab numbering.
          </HelperText>

          <CountRow>
            <FieldGroup style={{ marginBottom: 0, minWidth: 160 }}>
              <FieldLabel htmlFor="bundle-count">
                Number of Subfolders
              </FieldLabel>
              <StyledInput
                type="number"
                min={1}
                max={5}
                value={bundleCount}
                onChange={handleCountChange}
              />
              {error && <ErrorLabel>{error}</ErrorLabel>}
            </FieldGroup>
          </CountRow>

          <BundleConfigGrid>
            {Array.from({ length: count }, (_, i) => {
              const code = String.fromCharCode(65 + i);
              const cfg = bundleConfigs[i] || { subFolderName: "", prefix: "" };
              return (
                <BundleConfigCard key={i}>
                  <BundleCodeBadge>Sub Folder {code}</BundleCodeBadge>

                  <BundleFieldRow>
                    <FieldGroup style={{ flex: 1, marginBottom: 0 }}>
                      <FieldLabel htmlFor={`sf-name-${i}`}>
                        Sub Folder Name {i + 1} *
                      </FieldLabel>
                      <StyledInput
                        variant="outlined"
                        id={`sf-name-${i}`}
                        value={cfg.subFolderName}
                        onChange={(e) =>
                          updateConfig(i, "subFolderName", e.target.value)
                        }
                        placeholder={`e.g. Pleadings & Orders`}
                      />
                    </FieldGroup>

                    <FieldGroup style={{ width: 140, marginBottom: 0 }}>
                      <FieldLabel htmlFor={`prefix-${i}`}>
                        Prefix {i + 1} *
                      </FieldLabel>
                      <StyledInput
                        variant="outlined"
                        id={`prefix-${i}`}
                        value={cfg.prefix}
                        onChange={(e) =>
                          updateConfig(i, "prefix", e.target.value)
                        }
                        placeholder={`e.g. ${code}`}
                      />
                    </FieldGroup>
                  </BundleFieldRow>

                  {(cfg.subFolderName || cfg.prefix) && (
                    <PreviewPill>
                      <Icon.Layers size={12} color={T.tealDark} />
                      {cfg.prefix ? `[${cfg.prefix}]` : `[${code}]`}{" "}
                      {cfg.subFolderName || `Sub Folder ${code}`}
                    </PreviewPill>
                  )}
                </BundleConfigCard>
              );
            })}
          </BundleConfigGrid>
        </SectionDivider>
      )}
    </Container>
  );
};

export default Step1NamePurpose;

/* ── Styled Components ── */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FieldGroup = styled.div`
  margin-bottom: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-family: ${FONTS.primaryFontSemiBold};
  font-size: 12px;
  letter-spacing: 0.08em;
  color: ${T.mute};
  margin-bottom: 8px;
`;

const Grid3 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  @media (max-width: ${BP.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 6px;
`;

const CheckboxItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckboxHelperText = styled.p`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.sm};
  color: ${T.mute};
  margin: 2px 0 0 32px;
  line-height: 1.4;
`;

const SectionDivider = styled.div`
  border-top: 1px solid ${T.line};
  padding-top: 24px;
  margin-top: 8px;
`;

const SectionLabel = styled.div`
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${T.mute};
  text-transform: uppercase;
  margin-bottom: 10px;
`;

const HelperText = styled.p`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.base};
  color: ${T.mute};
  margin: 0 0 20px;
  line-height: 1.55;
`;

const CountRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 24px;
`;

const BundleConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
`;

const BundleConfigCard = styled.div`
  padding: 18px;
  border-radius: 12px;
  border: 1.5px solid ${T.line};
  background: ${T.white};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const BundleCodeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${T.tealDark};
  background: ${T.tealTint};
  padding: 4px 10px;
  border-radius: 6px;
  align-self: flex-start;
`;

const BundleFieldRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  @media (max-width: ${BP.mobile}) {
    flex-direction: column;
  }
`;

const PreviewPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 7px;
  background: ${T.tealTint};
  color: ${T.tealDark};
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  align-self: flex-start;
`;
